import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import {
  type MoveTarget,
  type MoveVerdict,
} from '../../../../helpers/knowledge-base/fileSystemMove.js';
import {
  isPendingFolder,
  splitFileSystemNodes,
} from '../../../../helpers/knowledge-base/fileSystemNode.js';
import { useBrowsedFolderId } from '../../../../hooks/knowledge-base/useBrowsedFolderId.js';
import { useFileSystemNodes } from '../../../../hooks/knowledge-base/useFileSystemNodes.js';
import { useSearchValue } from '../../../../hooks/useSearchValue.js';
import { toListedFolderId } from '../../../../queries/knowledgeBase.js';
import {
  canNestAt,
  type FileSystemNode,
  type FolderNode,
} from '../../../../types/fileSystem.js';
import { FileSystemBreadcrumbs } from '../Breadcrumbs/Breadcrumbs.jsx';
import { MovePickerListing } from './MovePickerListing.jsx';

const MoveNodesDialog = ({
  nodes,
  open,
  onClose,
  onMoveNodes,
  canMoveNodes,
}: {
  nodes: FileSystemNode[];
  open: boolean;
  onClose: () => void;
  onMoveNodes: (
    nodes: FileSystemNode[],
    target: MoveTarget,
  ) => Promise<boolean>;
  canMoveNodes: (nodes: FileSystemNode[], target: MoveTarget) => MoveVerdict;
}) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const {
    value: searchValue,
    debouncedValue: debouncedSearchValue,
    onChange: handleChangeSearch,
    onClear: handleClearSearch,
  } = useSearchValue();

  const [browsedFolderId, setCurrentFolderId] = useBrowsedFolderId();
  const currentFolderId = toListedFolderId(browsedFolderId);

  const {
    folders,
    files,
    breadcrumb,
    breadcrumbItems,
    depth,
    isLoading,
    ownPrivateBucket: privateBucket,
  } = useFileSystemNodes({
    folderId: currentFolderId,
    search: debouncedSearchValue,
  });

  const isRoot = depth === 0;
  const privateBucketLabel = t('My private folder');

  const target = useMemo<MoveTarget>(
    () => ({ folderId: currentFolderId, breadcrumb, folders }),
    [currentFolderId, breadcrumb, folders],
  );

  const movingIds = useMemo(
    () => new Set(nodes.map((node) => node.id)),
    [nodes],
  );

  // At the root the viewer's own private bucket leads, then the shared ones.
  const listedFolders = useMemo(() => {
    const listed = folders.filter((folder) => !isPendingFolder(folder));
    const query = debouncedSearchValue.trim().toLowerCase();

    if (
      !isRoot ||
      !privateBucket ||
      !privateBucketLabel.toLowerCase().includes(query)
    ) {
      return listed;
    }

    return [privateBucket, ...listed];
  }, [
    folders,
    isRoot,
    privateBucket,
    privateBucketLabel,
    debouncedSearchValue,
  ]);

  // Buckets only list folders; files appear for context once inside one.
  const listedFiles = isRoot ? [] : files;

  // Listed folders sit one level below the browsed one; the moved subtree's
  // full depth is the server's call.
  const canContainSubfolder = canNestAt(depth + 1);

  const [isMoving, setIsMoving] = useState(false);

  const handleMoveNode = async () => {
    setIsMoving(true);

    if (await onMoveNodes(nodes, target)) {
      onClose();
      return;
    }

    setIsMoving(false);
  };

  const onOpenFolder = useCallback(
    (folder: FolderNode) => setCurrentFolderId(folder.id),
    [setCurrentFolderId],
  );

  if (!nodes.length) {
    return null;
  }

  const {
    foldersCount,
    filesCount,
    files: movedFiles,
    folders: movedFolders,
  } = splitFileSystemNodes(nodes);

  const hasOnlyFiles = foldersCount === 0 && filesCount > 0;

  const getDialogLabel = () => {
    if (foldersCount > 0 && filesCount > 0) {
      return t('Move items');
    }

    if (foldersCount > 0) {
      if (foldersCount === 1) {
        return t('Move {{folderName}}', {
          folderName: [...movedFolders][0].name,
        });
      }

      return t('Move folders');
    }

    if (filesCount > 0) {
      if (filesCount === 1) {
        return t('Move {{fileName}}', {
          fileName: [...movedFiles][0].fileName,
        });
      }

      return t('Move files');
    }

    return '';
  };

  const isMoveButtonDisabled =
    isLoading || isMoving || !canMoveNodes(nodes, target).allowed;

  return (
    <Dialog modal onOpenChange={isMoving ? undefined : onClose} open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          aria-describedby={undefined}
          size="s"
          className="max-h-[90vh]"
        >
          <DialogHeader>
            <DialogTitle className="flex min-w-0 pr-8">
              <TruncateText>{getDialogLabel()}</TruncateText>
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <SearchBar
              placeholder={t('Search a folder')}
              className="h-8 max-w-full"
              onChange={handleChangeSearch}
              onClear={handleClearSearch}
              value={searchValue}
            />
            {isRoot ? (
              <h3
                className={cn(
                  'uppercase',
                  typographyVariants({ variant: 'h5', color: 'primary' }),
                )}
              >
                {t('Folders')}
              </h3>
            ) : (
              <FileSystemBreadcrumbs
                items={breadcrumbItems}
                onFolderChange={setCurrentFolderId}
              />
            )}
            <MovePickerListing
              isLoading={isLoading}
              folders={listedFolders}
              files={listedFiles}
              privateBucketId={privateBucket?.id}
              privateBucketLabel={privateBucketLabel}
              movingIds={movingIds}
              onOpenFolder={
                canContainSubfolder || hasOnlyFiles ? onOpenFolder : undefined
              }
            />
          </DialogContent>
          <DialogFooter>
            <Button
              size="large"
              variant="text"
              onClick={onClose}
              disabled={isMoving}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              onClick={() => void handleMoveNode()}
              disabled={isMoveButtonDisabled}
            >
              {t('Move here')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { MoveNodesDialog };
